import { type ChangeEvent } from "react";

import { SelectChangeEvent } from "@mui/material";

import { SavedSearchAtom } from "@/src/state/dealer/dealerActiveAuctionSearchFiltersAtom";
import { VehicleType } from "@/src/state/dealer/dealerActiveVehicleFilterAtom";

import { addThousandsSeparator } from "@/src/utils/numberUtils";

export interface RangeValue {
  start?: number;
  end?: number;
}

export interface SearchFilters {
  bikeType?: string[];
  caravanSubtype?: string[];
  vehicleType?: VehicleType[];
  make?: string[];
  modelYear?: RangeValue;
  mileage?: RangeValue;
  fuelType?: string[];
  gearType?: string[];
  targetPrice?: RangeValue;
  distance?: number;
  sellerDelivers?: boolean;
}

const DEFAULT_VEHICLE_TYPES = [
  VehicleType.CAR,
  VehicleType.MOTO,
  VehicleType.CARAVAN
];

export const handleVehicleTypeToggle = (
  vehicleType: VehicleType,
  previousVehicles: VehicleType[]
): VehicleType[] => {
  if (vehicleType === VehicleType.ALL) {
    return [VehicleType.CAR, VehicleType.MOTO, VehicleType.CARAVAN];
  }

  return previousVehicles.length === 1 && previousVehicles.includes(vehicleType)
    ? previousVehicles
    : [vehicleType];
};

export const handleBikeTypeToggle = (
  event: ChangeEvent<HTMLInputElement>,
  currentFilters: SearchFilters
): SearchFilters => {
  const bikeTypeValues = currentFilters?.bikeType ?? [];
  const { checked, value } = event.target;

  return {
    ...currentFilters,
    bikeType: checked
      ? [...bikeTypeValues, value]
      : bikeTypeValues.filter((bikeType) => bikeType !== value)
  };
};

export const handleCaravanSubtypeToggle = (
  event: ChangeEvent<HTMLInputElement>,
  currentFilters: SearchFilters
): SearchFilters => {
  const caravanSubtypeValues = currentFilters?.caravanSubtype ?? [];
  const { checked, value } = event.target;

  return {
    ...currentFilters,
    caravanSubtype: checked
      ? [...caravanSubtypeValues, value]
      : caravanSubtypeValues.filter((subtype) => subtype !== value)
  };
};

export const handleMakeChange = (
  event: SelectChangeEvent<string[]>,
  currentFilters: SearchFilters
): SearchFilters => {
  const value =
    typeof event.target.value === "string"
      ? event.target.value.split(",")
      : event.target.value;

  return { ...currentFilters, make: value };
};

export const handleRangeChange = (
  field: keyof Pick<SearchFilters, "modelYear" | "mileage" | "targetPrice">,
  range: RangeValue,
  currentFilters: SearchFilters
): SearchFilters => ({
  ...currentFilters,
  [field]: range
});

export const handleCheckboxChange = (
  field: keyof Pick<SearchFilters, "fuelType" | "gearType">,
  event: ChangeEvent<HTMLInputElement>,
  currentFilters: SearchFilters
): SearchFilters => {
  const currentValues = currentFilters[field] ?? [];
  const { checked, value } = event.target;

  return {
    ...currentFilters,
    [field]: checked
      ? [...currentValues, value]
      : currentValues.filter((item) => item !== value)
  };
};

export const handleDistanceChange = (
  event: { target: { value: string } },
  currentFilters: SearchFilters
): SearchFilters => {
  const distance = Number.parseInt(event.target.value);

  if (distance === 600) {
    return { ...currentFilters, distance: 1000 };
  }

  if (distance > 0) {
    return { ...currentFilters, distance };
  }

  const { distance: _, ...filters } = currentFilters;
  return { ...filters, sellerDelivers: true };
};

export const handleSellerDeliversChange = (
  event: ChangeEvent<HTMLInputElement>,
  currentFilters: SearchFilters
): SearchFilters => ({
  ...currentFilters,
  sellerDelivers: event.target.checked
});

export const getModelYearChipText = (
  modelYear?: RangeValue
): string[] | undefined => {
  if (!modelYear) return undefined;
  const { start, end } = modelYear;

  if (start && !end) return [`> ${start}`];
  if (!start && end) return [`< ${end}`];
  if (start && end) return [`${start} - ${end}`];

  return undefined;
};

export const handleSearchFilterSelect = (
  selectedFilterName: string,
  currentFilterName: string,
  savedFilters?: Array<{ name: string; searchFilters: SearchFilters }>
): { filters: SearchFilters; newFilterName: string } => {
  if (selectedFilterName === currentFilterName) {
    return { filters: {}, newFilterName: "" };
  }

  const selectedFilter = savedFilters?.find(
    (filter) => filter.name === selectedFilterName
  );

  return {
    filters: selectedFilter?.searchFilters ?? {},
    newFilterName: selectedFilterName
  };
};

interface NotificationMessage {
  text: string;
  variant: "info" | "success" | "error" | "warning";
}

interface UpdateFilterResult {
  updatedFilters: SavedSearchAtom[] | undefined;
  notification: NotificationMessage;
}

export const handleUpdateFilter = (
  selectedSearchFilter: string,
  currentFilters: SearchFilters,
  savedFilters: SavedSearchAtom[] | undefined
): UpdateFilterResult | null => {
  const selectedFilter = savedFilters?.find(
    (filter) => filter.name === selectedSearchFilter
  );

  if (!selectedFilter) return null;

  const updatedFilters = savedFilters?.map((filter) =>
    filter.name === selectedSearchFilter
      ? { ...filter, searchFilters: currentFilters }
      : filter
  ) as SavedSearchAtom[];

  return {
    updatedFilters,
    notification: {
      text: "dealer.searchFilterMessages.searchFiltersUpdatedSuccessfully",
      variant: "info"
    }
  };
};

interface DeleteFilterResult {
  updatedFilters: SavedSearchAtom[] | undefined;
  notification: NotificationMessage;
}

export const handleDeleteSavedSearchFilter = (
  filterName: string,
  savedFilters: SavedSearchAtom[] | undefined
): DeleteFilterResult => {
  const updatedFilters = savedFilters?.filter(
    (filter) => filter.name !== filterName
  );

  return {
    updatedFilters,
    notification: {
      text: "dealer.searchFilterMessages.searchFilterDeletedSuccessfully",
      variant: "success"
    }
  };
};

export const getTargetPriceChipText = (
  start: number | undefined,
  end: number | undefined,
  maxTargetPrice: number
): string => {
  const rangeMinText = `${addThousandsSeparator(start)} €`;
  const rangeMaxText = end
    ? `${addThousandsSeparator(end)}`
    : `${addThousandsSeparator(maxTargetPrice)}+ €`;

  return `${rangeMinText} - ${rangeMaxText}`;
};

export const hasNonDefaultValues = (
  filters: SearchFilters | undefined
): boolean => {
  if (!filters) return false;

  const hasCustomVehicleTypes: boolean =
    !!filters.vehicleType &&
    (filters.vehicleType.length !== DEFAULT_VEHICLE_TYPES.length ||
      !filters.vehicleType.every((type) =>
        DEFAULT_VEHICLE_TYPES.includes(type)
      ));

  const hasOtherFilters: boolean =
    (filters.bikeType?.length ?? 0) > 0 ||
    (filters.caravanSubtype?.length ?? 0) > 0 ||
    (filters.make?.length ?? 0) > 0 ||
    !!filters.modelYear?.start ||
    !!filters.modelYear?.end ||
    !!filters.mileage?.start ||
    !!filters.mileage?.end ||
    (filters.fuelType?.length ?? 0) > 0 ||
    (filters.gearType?.length ?? 0) > 0 ||
    !!filters.targetPrice?.start ||
    !!filters.targetPrice?.end ||
    (filters.distance ?? 0) > 0 ||
    filters.sellerDelivers === false;

  return hasCustomVehicleTypes || hasOtherFilters;
};
